import cn from 'classnames';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import DeleteIcon from '../../../assets/images/icon-delete-button.svg';
import EditIcon from '../../../assets/images/icon-edit-button.svg';

import { DEPT_ADMIN, DISTRIBUTOR, ORG_ADMIN, SUPER_ADMIN } from '../../../constants/users';
import useWindowSize from '../../../hooks/useWindowSize';
import { selectAdminType, selectUserDepartmentId } from '../../../store/selectors/auth';
import { TCollectiveTemplateItem } from '../../../types/templates';
import { hasPermission } from '../../../utils/users';
import { TextIconButton } from '../../forms';

interface IUseTemplatePermissionsReturn {
  canManageTemplate: (template: TCollectiveTemplateItem) => boolean;
  renderControls: (template: TCollectiveTemplateItem, styles: Record<string, string>) => JSX.Element | null;
}

export const useTemplatePermissions = (
  onEdit: (template: TCollectiveTemplateItem) => void,
  onDelete: (template: TCollectiveTemplateItem) => void,
): IUseTemplatePermissionsReturn => {
  const { mobile } = useWindowSize();
  const adminType = useSelector(selectAdminType);
  const userDepartmentId = useSelector(selectUserDepartmentId);

  const canManageTemplate = useCallback(
    (template: TCollectiveTemplateItem) => {
      const { department_ids: depIds } = template;
      return Boolean(
        hasPermission([ORG_ADMIN, DISTRIBUTOR, SUPER_ADMIN], adminType) ||
          (hasPermission([DEPT_ADMIN], adminType) && userDepartmentId && depIds.includes(userDepartmentId)),
      );
    },
    [adminType, userDepartmentId],
  );

  const renderControls = useCallback(
    (template: TCollectiveTemplateItem, styles: Record<string, string>) => {
      /* Dep. admin - are able to manage templates for their department only
       * Org, dist and super admins - are able to manage templates within an organization they're in
       */
      if (!canManageTemplate(template)) {
        return null;
      }

      return (
        <div className={styles.controls}>
          <TextIconButton
            trigger={mobile}
            className={styles.actionBtn}
            icon={EditIcon}
            title="Edit"
            onClick={() => onEdit(template)}
            dataAction="edit_template_btn"
          />
          <TextIconButton
            trigger={mobile}
            className={cn(styles.actionBtn, styles.delete)}
            icon={DeleteIcon}
            title="Delete"
            onClick={() => onDelete(template)}
            dataAction="delete_template_btn"
          />
        </div>
      );
    },
    [mobile, onEdit, onDelete, canManageTemplate],
  );

  return {
    canManageTemplate,
    renderControls,
  };
};
